import  React from 'react'
import { Alert } from 'react-bootstrap'
import Dropzone from 'react-dropzone'
import { TableSectionProps } from './TableSection'
import { validateFields } from '../fields'
import { ImportContext } from '../../models/ImportContext'

export interface TableImportProps<D> {
    id: string
    value: D[]
    onChange: { (value: D[]): any }
    renderSection: { (p: TableSectionProps<D>, i: number): JSX.Element }
    importContext: ImportContext
    importUrl: string
    exportUrl: string
    match?: { (a: D, b: D): boolean }
    batchSize?: number
}

export interface TableImportState<D> {
    newValue: D[]
    inProgress: number[]
}

export class TableImport<D> extends React.Component<TableImportProps<D>, TableImportState<D>> {
    exportInput: HTMLInputElement

    constructor(props: TableImportProps<D>, context?: any) {
      super(props, context)

      this.state = {
        newValue: [],
        inProgress: [],
      }
    }

    import = (files: File[]) => {
      const formData = new FormData()
      for (const i in files) {
        formData.append('file', files[i])
      }
      if (this.props.importContext) {
        formData.append('context', JSON.stringify(this.props.importContext))
      }
      fetch(this.props.importUrl, {
        method: 'POST',
        body: formData,
      }).then((v) => {
        if (!v.ok) {
          alert('Error encountered while importing. Please check the data is in the correct format and try again.')
        }
        v.json().then((newValue: D[]) => {
          if (newValue.length == 0) { alert('No rows have been imported. Please check your data/template.') }

          const bacthSize = this.props.batchSize || 1
          const value: D[] = []
          this.props.onChange && this.props.onChange(value)
          const inProgress: number[] = []

          for (let i = 0; i < bacthSize; i++) {
            if (i >= newValue.length) {
              break
            }

            inProgress.push(i)
          }

          this.setState({ newValue, inProgress })
        })
      })
    }

    download = (e: React.MouseEvent<HTMLInputElement>) => {
      this.exportInput.value = ''
    }

    export = (e: React.MouseEvent<HTMLInputElement>) => {
      this.exportInput.value = JSON.stringify(this.props.value)
    }

    getInProgress(i: number) {
      const inProgress = this.state.inProgress.slice()

      inProgress.sort((a, b) => {
        if (a > b) {
          return -1
        }
        if (a < b) {
          return 1
        }

        return 0
      })

      const next = inProgress[0] + 1
      if (next < this.state.newValue.length) {
        inProgress.push(next)
      }

      inProgress.splice(inProgress.indexOf(i), 1)

      return inProgress
    }

    private complete = () => {
      if (this.state.inProgress.length > 0) {
        return
      }

      let value = this.props.value.slice()
      const newValues = this.state.newValue.slice()

      for (let i = 0; i < value.length; i++) {
        for (let j = 0; j < newValues.length; j++) {
          if (this.props.match && this.props.match(value[i], newValues[j])) {
            value.splice(i, 1, newValues[j])
            newValues.splice(j, 1)
          }
        }
      }

      value = [...value, ...newValues]

      this.props.onChange && this.props.onChange(value)
    }

    private onChange = (v: D, i: number, close?: boolean) => {
      setTimeout(() => {
        const newValue = this.state.newValue.slice()

        newValue.splice(i, 1, v)

        if (close) {
          const inProgress = this.getInProgress(i)
          this.setState({ newValue, inProgress }, () => {
            this.complete()
          })
        } else {
          this.setState({ newValue })
        }
      }, 2)
    }

    private onRemove = (i: number) => {
      setTimeout(() => {
        const newValue = this.state.newValue.slice()
        newValue.splice(i, 1)

        const inProgress = this.getInProgress(i)
        this.setState({ newValue, inProgress }, () => {
          this.complete()
        })
      }, 2)
    }

    private onClose = (i: number) => {
      setTimeout(() => {
        const inProgress = this.getInProgress(i)
        this.setState({ inProgress }, () => {
          this.complete()
        })
      })
    }

    render() {
      let content: JSX.Element

      if (this.state.inProgress.length == 0) {
        content = (
          <Dropzone multiple={false} onDrop={this.import}>
            {({
              getRootProps, getInputProps, isDragAccept, isDragReject,
            }) => (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <div className={'mt-3 form-file'
                            + `${isDragAccept ? ' form-file-valid' : ''}`
                            + `${isDragReject ? ' form-file-invalid' : ''}`}
                >
                  Drag and drop files here, or click to select files.
                  <br />
                  <p>Uploading a template deletes and replaces any existing data.</p>
                </div>
              </div>
            )}
          </Dropzone>
        )
      } else {
        const inProgress = this.state.inProgress
          .slice()
          .sort((a, b) => {
            if (a > b) {
              return -1
            }
            if (a < b) {
              return 1
            }

            return 0
          })

        const percent = Math.ceil((inProgress[0] / this.state.newValue.length) * 100)

        const aria = {
          'aria-valuenow': percent,
          'aria-valuemin': 0,
          'aria-valuemax': 100,
        }

        content = (
          <div className="form-progress">
            <div className="progress">
              <div className="progress-bar" role="progressbar" style={{ width: `${percent}%` }} {...aria} />
            </div>
          </div>
        )
      }

      return (
        <>
          <Alert className="emdg-page-alerts">
            <b>To use the Excel template below you must be familiar with Excel files, their requirements and how they work.</b>
            <br />
            Take care when copying and pasting data from other spreadsheets as this can lead to errors.
            <br />
            If you are not familiar with Excel operation, then key in your data directly into the fields below.
            <br />
            To download a template for this schedule click the "Download template" button.
            <p>Only import files created using the templates downloaded from this page.</p>
            <p><a href="https://www.austrade.gov.au/ArticleDocuments/6917/EMDG-CSV-instructions.pdf.aspx" target="_blank" rel="noreferrer">Instructions on how to prepare Excel files that can then be uploaded to this schedule (link opens a new browser window)</a></p>

            <form action={`${this.props.exportUrl}`} method="POST" className="mt-1" target="_blank">
              <input ref={(r) => this.exportInput = r} type="hidden" name="data" />
              <input type="submit" className="emdg-btn" onClick={this.download} value="Download template" />
              {' '}
              {this.props.value && this.props.value.length > 0 ? <input type="submit" className="emdg-btn" onClick={this.export} value="Export data" /> : ''}
            </form>

            <p>To upload your data from the template, first save the data as an Excel file, then drag and drop the file in the box below. Alternatively, clicking on the box below to navigate to the file on your computer, then select the file and upload it into the system.</p>
          </Alert>
          {content}
          {this.state.inProgress.map((r) => (
            <div key={r} style={{ display: 'none' }}>
              {this.props.renderSection({
                ref: (r) => { },
                id: `${this.props.id}-${r}`,
                model: this.state.newValue[r],
                tableModel: this.props.value.concat(this.state.newValue), // ?????
                onChange: (v, c) => this.onChange(v, r, c),
                onRemove: () => this.onRemove(r),
                onClose: () => this.onClose(r),
                importing: true,
                preview: false,
              }, r)}
            </div>
          ))}
        </>
      )
    }
}
